import { useEffect } from 'react';
import { LockIcon, XCloseModal } from '../../../icons/SvgIcons';
import { SideDrawerProps } from './SideDrawer.types';

export function SideDrawer({
  isOpen,
  onClose,
  title,
  status,
  locked,
  showActionButton,
  overview,
  requiredActions,
  actionButton,
  listItems,
  listItemsClasses = '',
  onScrollContent
}: SideDrawerProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const Divider = (className: string = '') => (
    <div className={`border-b border-gray-300 my-[14px] ${className}`} />
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[448px] bg-white z-50 shadow-xl
          transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full text-left">
          {/* Group 1: Title and Close Icon - Fixed at top */}
          <div className="flex items-center justify-between p-6 pb-0">
            <h2 className="text-[18px] font-medium text-gray-900v2">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Close drawer"
              id="side-drawer-close-button"
              data-testid="side-drawer-close-button"
            >
              {XCloseModal()}
            </button>
          </div>
          {Divider('mx-6')}

          {/* Scrollable Content */}
          <div
            className="flex-1 overflow-y-auto px-6 pt-0"
            onScroll={onScrollContent}
          >
            {/* Group 2: Status */}
            {status && (
              <>
                <div className="">
                  <h3 className="text-[12px] font-medium text-gray-550 uppercase mb-2">
                    Status:
                  </h3>
                  {status}
                </div>
                {overview && Divider()}
              </>
            )}

            {/* Group 3: Overview */}
            {overview && (
              <>
                <div className="">
                  <h3 className="text-[12px] font-medium text-gray-550 uppercase mb-3">
                    Overview:
                  </h3>
                  {overview}
                </div>
                {requiredActions && Divider()}
              </>
            )}

            {/* Group 4: Required Actions */}
            {requiredActions && (
              <>
                <div className="">
                  <h3 className="text-[12px] font-medium text-gray-550 uppercase mb-3">
                    Required Actions:
                  </h3>
                  {requiredActions}
                </div>
              </>
            )}

            {/* Locker banner */}
            {locked && (
              <div>
                {Divider()}
                <div className="flex flex-row items-start gap-2 self-stretch p-2 border rounded-md border-gray-100 bg-gray-50">
                  <div className="w-6 h-6 min-w-[24px] min-h-[24px] flex items-center justify-center bg-gray-100 rounded-full overflow-hidden shrink-0">
                    {LockIcon('#4E4E56')}
                  </div>
                  <div className="text-gray-900v2 text-sm font-normal">
                    This section is locked. Please complete previous steps to
                    unlock.
                  </div>
                </div>
              </div>
            )}

            {/* Group 5: List Items */}
            {listItems &&
              listItems.map((item, index) => (
                <div key={index} className={listItemsClasses}>
                  {item}
                  {index !== listItems.length - 1 && Divider()}
                </div>
              ))}
          </div>

          {/* Group 6: Action Button - Fixed at bottom */}
          {showActionButton && actionButton && (
            <div className="px-6 pb-6 pt-0 text-right">
              {/* Divider  */}
              {Divider()}
              {/* Action Button */}
              {actionButton}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
